import { useState } from 'react';

export const ALLERGENS = ['Gluten', 'Dairy', 'Eggs', 'Fish', 'Shellfish', 'Tree Nuts', 'Peanuts', 'Soy', 'Sesame'];
export const TEMPERATURES = ['Rare', 'Medium Rare', 'Medium', 'Medium Well', 'Well Done'];
const PRICE_RE = /^\d{1,5}(\.\d{1,2})?$/;

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-400';

/**
 * Create / edit one menu item: every D6 field. `item` is null for a new item.
 * onSubmit(body) receives the API body (snake_case wine_pairing, is_available).
 */
export default function ItemForm({ item, categories, defaultCategory, onSubmit, onCancel, busy }) {
    const known = new Set(ALLERGENS.map((a) => a.toLowerCase()));
    const [form, setForm] = useState(() => ({
        name: item?.name || '',
        price: item ? String(item.price) : '',
        category: item?.category || defaultCategory || categories[0]?.id || '',
        description: item?.description || '',
        ingredients: item?.ingredients || '',
        preparation: item?.preparation || '',
        winePairing: item?.winePairing || '',
        allergens: (item?.allergens || []).filter((a) => known.has(a.toLowerCase())).map((a) => ALLERGENS.find((x) => x.toLowerCase() === a.toLowerCase())),
        otherAllergens: (item?.allergens || []).filter((a) => !known.has(a.toLowerCase())).join(', '),
        temperatures: item?.temperatures || [],
        isAvailable: item ? item.isAvailable !== false : true,
    }));
    const [error, setError] = useState('');
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

    const submit = (e) => {
        e.preventDefault();
        const price = form.price.trim().replace(/^\$/, '');
        if (!form.name.trim() || form.name.trim().length > 100) return setError('Name is required (up to 100 characters).');
        if (!PRICE_RE.test(price)) return setError('Price must be a number like 12.50 (0 to 99999.99).');
        if (!form.category) return setError('Pick a category.');
        setError('');
        const other = form.otherAllergens.split(',').map((s) => s.trim()).filter(Boolean);
        onSubmit({
            name: form.name.trim(),
            price,
            category: form.category,
            description: form.description.trim() || null,
            ingredients: form.ingredients.trim() || null,
            preparation: form.preparation.trim() || null,
            wine_pairing: form.winePairing.trim() || null,
            allergens: [...form.allergens, ...other],
            temperatures: TEMPERATURES.filter((t) => form.temperatures.includes(t)),
            is_available: form.isAvailable,
        });
    };

    const field = (id, label, input) => (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            {input}
        </div>
    );

    return (
        <form onSubmit={submit} aria-label={item ? `Edit ${item.name}` : 'New menu item'} className="bg-white rounded-xl border border-purple-200 p-4 shadow space-y-3">
            <h4 className="font-bold text-gray-900">{item ? `Edit ${item.name}` : 'New item'}</h4>
            <div className="grid gap-3 sm:grid-cols-2">
                {field('item-name', 'Name', <input id="item-name" className={inputCls} value={form.name} maxLength={100} onChange={(e) => set('name', e.target.value)} required />)}
                {field('item-price', 'Price', <input id="item-price" className={inputCls} value={form.price} inputMode="decimal" placeholder="12.50" onChange={(e) => set('price', e.target.value)} required />)}
                {field('item-category', 'Category', (
                    <select id="item-category" className={inputCls} value={form.category} onChange={(e) => set('category', e.target.value)}>
                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                ))}
                {field('item-wine', 'Wine pairing', <input id="item-wine" className={inputCls} value={form.winePairing} maxLength={200} onChange={(e) => set('winePairing', e.target.value)} />)}
            </div>
            {field('item-description', 'Description', <textarea id="item-description" rows={2} className={inputCls} value={form.description} maxLength={1000} onChange={(e) => set('description', e.target.value)} />)}
            {field('item-ingredients', 'Ingredients', <textarea id="item-ingredients" rows={2} className={inputCls} value={form.ingredients} maxLength={1000} onChange={(e) => set('ingredients', e.target.value)} />)}
            {field('item-preparation', 'Preparation', <textarea id="item-preparation" rows={2} className={inputCls} value={form.preparation} maxLength={1000} onChange={(e) => set('preparation', e.target.value)} />)}

            <fieldset>
                <legend className="text-sm font-medium text-gray-700 mb-1">Allergens</legend>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {ALLERGENS.map((a) => (
                        <label key={a} className="flex items-center gap-1.5 text-gray-800">
                            <input type="checkbox" checked={form.allergens.includes(a)} onChange={() => toggle('allergens', a)} /> {a}
                        </label>
                    ))}
                </div>
                <label htmlFor="item-other-allergens" className="block text-sm text-gray-600 mt-2">Other allergens (comma separated)</label>
                <input id="item-other-allergens" className={inputCls} value={form.otherAllergens} onChange={(e) => set('otherAllergens', e.target.value)} />
            </fieldset>

            <fieldset>
                <legend className="text-sm font-medium text-gray-700 mb-1">Temperatures (steaks and other cooked-to-order items)</legend>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {TEMPERATURES.map((t) => (
                        <label key={t} className="flex items-center gap-1.5 text-gray-800">
                            <input type="checkbox" checked={form.temperatures.includes(t)} onChange={() => toggle('temperatures', t)} /> {t}
                        </label>
                    ))}
                </div>
            </fieldset>

            <label className="flex items-center gap-2 text-gray-800">
                <input type="checkbox" checked={form.isAvailable} onChange={(e) => set('isAvailable', e.target.checked)} />
                Available (shown on the POS)
            </label>

            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            <div className="flex gap-3">
                <button type="submit" disabled={busy} className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-purple-400">
                    {item ? 'Save item' : 'Add item'}
                </button>
                <button type="button" onClick={onCancel} className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400">
                    Cancel
                </button>
            </div>
        </form>
    );
}
